import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Lottie from 'lottie-react';
import useTranslations from '../../Hooks/useTranslations';
import translations from './translations.json';
import birdAnimation from './bird.json';
import './NoInternetScreen.css';

/* ------------------------------------------------------------------ */
/*  Scanning dots animation                                            */
/* ------------------------------------------------------------------ */

const ScanningDots = () => (
  <div className="no-internet-scanning">
    {[0, 1, 2].map((i) => (
      <motion.span
        key={i}
        className="no-internet-scanning-dot"
        animate={{ opacity: [0.2, 1, 0.2] }}
        transition={{
          duration: 1.4,
          delay: i * 0.2,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />
    ))}
  </div>
);

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

const NoInternetScreen = () => {
  const t = useTranslations(translations);
  const [retrying, setRetrying] = useState(false);
  const [showTip, setShowTip] = useState(false);

  useEffect(() => {
    const tipTimer = setTimeout(() => setShowTip(true), 5000);
    return () => clearTimeout(tipTimer);
  }, []);

  useEffect(() => {
    const crispEl = document.querySelector('.crisp-client');
    if (crispEl) crispEl.style.display = 'none';
    return () => {
      if (crispEl) crispEl.style.display = '';
    };
  }, []);

  const handleRetry = useCallback(() => {
    setRetrying(true);
    // The hook will detect connectivity automatically;
    // this is just a visual indicator + force check
    setTimeout(() => setRetrying(false), 3000);
  }, []);

  return (
    <AnimatePresence>
      <motion.div
        className="no-internet-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Animated background blobs */}
        <div className="no-internet-bg-blobs">
          <motion.div
            className="no-internet-blob no-internet-blob-1"
            animate={{
              x: [0, 30, -20, 0],
              y: [0, -40, 20, 0],
              scale: [1, 1.1, 0.95, 1],
            }}
            transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.div
            className="no-internet-blob no-internet-blob-2"
            animate={{
              x: [0, -40, 30, 0],
              y: [0, 20, -30, 0],
              scale: [1, 0.9, 1.15, 1],
            }}
            transition={{ duration: 25, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.div
            className="no-internet-blob no-internet-blob-3"
            animate={{
              x: [0, 20, -30, 0],
              y: [0, 30, -20, 0],
              scale: [1, 1.05, 0.9, 1],
            }}
            transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
          />
        </div>

        {/* Content card */}
        <motion.div
          className="no-internet-card"
          initial={{ y: 40, opacity: 0, scale: 0.95 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.2, type: 'spring', damping: 20 }}
        >
          <Lottie
            animationData={birdAnimation}
            loop
            className="no-internet-jellyfish"
          />

          <motion.h1
            className="no-internet-title"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.5 }}
          >
            {t.title}
          </motion.h1>

          <motion.p
            className="no-internet-subtitle"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.7, duration: 0.5 }}
          >
            {t.subtitle}
          </motion.p>

          <motion.div
            className="no-internet-status"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.9, duration: 0.5 }}
          >
            <div className="no-internet-status-indicator" />
            <span>{t.status}</span>
            <ScanningDots />
          </motion.div>

          <motion.button
            className="no-internet-retry-btn"
            onClick={handleRetry}
            disabled={retrying}
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 1.1, duration: 0.5 }}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
          >
            {retrying ? (
              <motion.span
                className="no-internet-retry-spinner"
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              >
                &#8635;
              </motion.span>
            ) : (
              t.retryButton
            )}
          </motion.button>

          <AnimatePresence>
            {showTip && (
              <motion.div
                className="no-internet-tips"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.4 }}
              >
                <p className="no-internet-tips-title">{t.tipsTitle}</p>
                <ul className="no-internet-tips-list">
                  <li>{t.tip1}</li>
                  <li>{t.tip2}</li>
                  <li>{t.tip3}</li>
                  <li>{t.tip4}</li>
                </ul>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Bottom wave decoration */}
        <div className="no-internet-wave-container">
          <svg
            className="no-internet-wave"
            viewBox="0 0 1440 120"
            preserveAspectRatio="none"
          >
            <motion.path
              d="M0,60 C360,120 720,0 1080,60 C1260,90 1380,80 1440,60 L1440,120 L0,120 Z"
              fill="rgba(72, 170, 175, 0.08)"
              animate={{
                d: [
                  'M0,60 C360,120 720,0 1080,60 C1260,90 1380,80 1440,60 L1440,120 L0,120 Z',
                  'M0,80 C360,20 720,100 1080,40 C1260,20 1380,60 1440,80 L1440,120 L0,120 Z',
                  'M0,60 C360,120 720,0 1080,60 C1260,90 1380,80 1440,60 L1440,120 L0,120 Z',
                ],
              }}
              transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
            />
            <motion.path
              d="M0,80 C360,40 720,100 1080,70 C1260,55 1380,90 1440,80 L1440,120 L0,120 Z"
              fill="rgba(72, 170, 175, 0.05)"
              animate={{
                d: [
                  'M0,80 C360,40 720,100 1080,70 C1260,55 1380,90 1440,80 L1440,120 L0,120 Z',
                  'M0,60 C360,100 720,30 1080,80 C1260,95 1380,50 1440,60 L1440,120 L0,120 Z',
                  'M0,80 C360,40 720,100 1080,70 C1260,55 1380,90 1440,80 L1440,120 L0,120 Z',
                ],
              }}
              transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
            />
          </svg>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default NoInternetScreen;
